import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { api } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import { Check, X, Loader2, AlertCircle } from 'lucide-react'
import { ProposalEditor, isPayloadValid } from './ProposalEditors'

const TYPE_LABELS = {
  announcement: 'Announcement',
  mass_times: 'Mass Times',
  confession_times: 'Confession Times',
  staff: 'Parish Staff',
  event: 'Event',
  parish_field: 'Parish Info',
}

function ProposalCard({ proposal, onApprove, onReject, current }) {
  const [loading, setLoading] = useState(null)
  const [draft, setDraft] = useState(proposal.payload ?? {})

  const isDone = proposal.status !== 'pending'
  const valid = isPayloadValid(proposal.type, draft)

  async function handleApprove() {
    setLoading('approve')
    await onApprove(proposal.id, draft)
    setLoading(null)
  }

  async function handleReject() {
    setLoading('reject')
    await onReject(proposal.id)
    setLoading(null)
  }

  return (
    <div className="bg-white border border-stone-200 rounded p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <span className="inline-block text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 mb-2">
            {TYPE_LABELS[proposal.type] ?? proposal.type}
          </span>
          <p className="text-stone-800 text-sm leading-relaxed">{proposal.summary}</p>
        </div>

        {isDone && (
          <span className={`shrink-0 text-xs font-semibold px-3 py-1 rounded-full ${
            proposal.status === 'applied'
              ? 'bg-green-100 text-green-700'
              : proposal.status === 'rejected'
              ? 'bg-stone-100 text-stone-500'
              : 'bg-red-100 text-red-700'
          }`}>
            {proposal.status === 'applied' ? 'Approved' : proposal.status === 'rejected' ? 'Rejected' : 'Failed'}
          </span>
        )}
      </div>

      <div className="border-t border-stone-100 pt-4">
        <ProposalEditor
          proposal={proposal}
          value={draft}
          onChange={setDraft}
          disabled={isDone || loading !== null}
          current={current}
        />
      </div>

      {!isDone && (
        <div className="flex items-center justify-end gap-2 mt-4">
          {!valid && (
            <span className="text-xs text-stone-400 mr-auto">
              Fill in the required fields to approve.
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleReject}
            disabled={loading !== null}
            className="text-stone-600 border-stone-300 hover:bg-stone-50"
          >
            {loading === 'reject' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
            <span className="ml-1.5">Reject</span>
          </Button>
          <Button
            size="sm"
            onClick={handleApprove}
            disabled={loading !== null || !valid}
            className="bg-stone-900 hover:bg-stone-700 text-white"
          >
            {loading === 'approve' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            <span className="ml-1.5">Approve</span>
          </Button>
        </div>
      )}
    </div>
  )
}

export default function ReviewChanges() {
  const { batchId } = useParams()
  const [searchParams] = useSearchParams()
  const parishId = searchParams.get('parishId')

  const [proposals, setProposals] = useState([])
  const [parish, setParish] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchProposals = useCallback(async () => {
    try {
      // Fetch current parish alongside proposals so the editors can show only
      // what's changing. A parish-fetch failure is non-fatal — editors then show
      // every entry (no diff).
      const [data, parishData] = await Promise.all([
        api.getChangeProposals(parishId, batchId),
        api.getParish(parishId).catch(() => null),
      ])
      setProposals(data)
      setParish(parishData)
    } catch (err) {
      setError(err.message || 'Failed to load proposals.')
    } finally {
      setLoading(false)
    }
  }, [parishId, batchId])

  useEffect(() => {
    if (!parishId || !batchId) {
      setError('Invalid review link — missing parish or batch ID.')
      setLoading(false)
      return
    }
    fetchProposals()
  }, [batchId, parishId, fetchProposals])

  async function handleApprove(proposalId, payload) {
    try {
      await api.approveProposal(proposalId, payload)
      setProposals(prev =>
        prev.map(p =>
          p.id === proposalId
            ? { ...p, status: 'applied', payload: payload ?? p.payload }
            : p
        )
      )
    } catch (err) {
      setError(err.message || 'Failed to approve change.')
    }
  }

  async function handleReject(proposalId) {
    try {
      await api.rejectProposal(proposalId)
      setProposals(prev =>
        prev.map(p => p.id === proposalId ? { ...p, status: 'rejected' } : p)
      )
    } catch (err) {
      setError(err.message || 'Failed to reject change.')
    }
  }

  const parishInfo = parish?.parishInfo ?? {}
  function currentFor(type) {
    if (type === 'staff') return parishInfo.parishStaff
    if (type === 'mass_times') return parishInfo.massTimes
    if (type === 'confession_times') return parishInfo.confessionTimes
    return undefined
  }

  const pending = proposals.filter(p => p.status === 'pending').length
  const done = proposals.length - pending

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-serif text-stone-900 mb-1">Review Proposed Changes</h1>
          <p className="text-sm text-stone-500">
            {proposals.length === 0
              ? 'No proposals found for this batch.'
              : `${proposals.length} proposed change${proposals.length !== 1 ? 's' : ''} — ${pending} pending, ${done} reviewed`}
          </p>
        </div>

        {error && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-4 mb-6 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {proposals.length === 0 && !error && (
          <p className="text-stone-400 text-sm">No changes were proposed for this batch.</p>
        )}

        <div className="flex flex-col gap-3">
          {proposals.map(proposal => (
            <ProposalCard
              key={proposal.id}
              proposal={proposal}
              onApprove={handleApprove}
              onReject={handleReject}
              current={currentFor(proposal.type)}
            />
          ))}
        </div>

        {done === proposals.length && proposals.length > 0 && (
          <p className="mt-8 text-center text-sm text-stone-400">
            All changes have been reviewed. Your parish page has been updated.
          </p>
        )}
      </div>
    </div>
  )
}
